import { useEffect, useState } from "react";
import { Avatar, type AvatarShape } from "../../components/Avatar.js";
import { Icon } from "../../components/Icon.js";
import { useUser } from "../../auth/UserContext.js";
import { fmt } from "../../lib/money.js";
import { listActivity, type ActivityRow } from "../../api/activity.js";
import { deleteTransaction } from "../../api/transactions.js";
import type { ParentBundle } from "../../api/me.js";

const KNOWN_TYPES: ReadonlyArray<string> = [
  "deposit",
  "withdrawal",
  "allowance",
  "interest",
  "chores",
  "gift",
  "transfer",
  "adjustment",
];

export default function Activity() {
  const { bundle } = useUser();
  const [filterKid, setFilterKid] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [continuationToken, setContinuationToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const opts: { kidId?: string; type?: string; limit: number } = { limit: 50 };
        if (filterKid !== "all") opts.kidId = filterKid;
        if (filterType !== "all") opts.type = filterType;
        const res = await listActivity(opts);
        if (cancelled) return;
        setRows(res.rows);
        setContinuationToken(res.continuationToken);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load activity");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [filterKid, filterType]);

  const loadMore = async () => {
    if (!continuationToken) return;
    setLoading(true);
    try {
      const opts: {
        kidId?: string;
        type?: string;
        limit: number;
        continuationToken: string;
      } = { limit: 50, continuationToken };
      if (filterKid !== "all") opts.kidId = filterKid;
      if (filterType !== "all") opts.type = filterType;
      const res = await listActivity(opts);
      setRows((prev) => {
        const seen = new Set(prev.map((r) => `${r.partitionKey}/${r.rowKey}`));
        const fresh = res.rows.filter((r) => !seen.has(`${r.partitionKey}/${r.rowKey}`));
        return [...prev, ...fresh];
      });
      setContinuationToken(res.continuationToken);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (t: ActivityRow) => {
    setDeleting(true);
    try {
      await deleteTransaction(t.partitionKey, t.rowKey);
      setRows((prev) =>
        prev.filter(
          (r) =>
            !(r.partitionKey === t.partitionKey && r.rowKey === t.rowKey) &&
            !(t.idempotencyKey && r.idempotencyKey === t.idempotencyKey),
        ),
      );
    } finally {
      setDeleting(false);
      setConfirmKey(null);
    }
  };

  if (!bundle || bundle.user.role !== "parent") return null;
  const parent = bundle as ParentBundle;
  const family = parent.family;
  const kidById = new Map(family.map((k) => [k.user.rowKey, k.user]));

  return (
    <div>
      <h1 style={{ marginBottom: 20 }}>All activity</h1>
      <div className="row" style={{ marginBottom: 18, gap: 8, flexWrap: "wrap" }}>
        <div className="seg">
          <button
            className={filterKid === "all" ? "active" : ""}
            onClick={() => setFilterKid("all")}
          >
            All kids
          </button>
          {family.map((k) => (
            <button
              key={k.user.rowKey}
              className={filterKid === k.user.rowKey ? "active" : ""}
              onClick={() => setFilterKid(k.user.rowKey)}
            >
              {k.user.name}
            </button>
          ))}
        </div>
        <div className="seg">
          <button
            className={filterType === "all" ? "active" : ""}
            onClick={() => setFilterType("all")}
          >
            All types
          </button>
          {KNOWN_TYPES.map((t) => (
            <button
              key={t}
              className={filterType === t ? "active" : ""}
              onClick={() => setFilterType(t)}
            >
              <span style={{ textTransform: "capitalize" }}>{t}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        {error && (
          <div className="center soft" style={{ padding: 40, color: "var(--neg)" }}>
            {error}
          </div>
        )}
        {!error && loading && rows.length === 0 && (
          <div className="center soft" style={{ padding: 40 }}>
            Loading…
          </div>
        )}
        {!error && !loading && rows.length === 0 && (
          <div className="center soft" style={{ padding: 40 }}>
            No transactions match these filters.
          </div>
        )}
        {rows.map((t) => {
          const kidUser = kidById.get(t.partitionKey);
          const color = kidUser?.color ?? "var(--brand)";
          const name = kidUser?.name ?? t.partitionKey;
          const avatarShape = (kidUser?.avatarShape as AvatarShape | undefined) ?? "circle";
          const pos = t.amountCents > 0;
          const key = `${t.partitionKey}/${t.rowKey}`;
          const isConfirming = confirmKey === key;
          return (
            <div
              key={key}
              className="txn-row"
              style={{ gridTemplateColumns: "44px auto 1fr auto auto" }}
            >
              <Avatar kid={{ name, color, avatarShape }} size={36} />
              <div style={{ fontWeight: 500, fontSize: 14, minWidth: 70 }}>{name}</div>
              <div>
                <div style={{ fontSize: 14 }}>{t.note}</div>
                <div className="soft" style={{ fontSize: 12, textTransform: "capitalize" }}>
                  {t.type} ·{" "}
                  {new Date(t.date).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
              </div>
              <div className={`money money-sm ${pos ? "money-pos" : "money-neg"}`}>
                {fmt(t.amountCents, { showSign: true })}
              </div>
              {isConfirming ? (
                <div className="row" style={{ gap: 6 }}>
                  <button
                    className="btn btn-ghost"
                    style={{ fontSize: 12, padding: "4px 10px", color: "var(--neg)" }}
                    onClick={() => void handleDelete(t)}
                    disabled={deleting}
                  >
                    {deleting ? "…" : "Revert"}
                  </button>
                  <button
                    className="btn btn-ghost"
                    style={{ fontSize: 12, padding: "4px 10px" }}
                    onClick={() => setConfirmKey(null)}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  className="btn btn-ghost"
                  style={{ padding: 6, color: "var(--ink-mute)" }}
                  onClick={() => setConfirmKey(key)}
                  aria-label="Revert transaction"
                >
                  <Icon name="trash" size={16} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {continuationToken && (
        <div className="row" style={{ justifyContent: "center", marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={loadMore} disabled={loading}>
            {loading ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}
